import React from "react";
import { Button } from "reactstrap";
import NavHeader from "./NavHeader";
import NavRow from "./NavRow";
import PlanillaMicrofonoModal from "./PlanillaMicrofonoModal";
import CartelesCanchasModal from "./CartelesCanchasModal";
import PlanillaArbitrosModal from "./PlanillaArbitrosModal";
import FichasArbitrosModal from "./FichasArbitrosModal";
import { genId, nuevoObjectId } from "./ids";
import { toastConfirm } from "components/utils/toasts";
import { jugados, partidosDeFechas, textoArrastre } from "./bajas";

// Una rueda: nombre + lista de zonas. Cada zona abre su detalle en drill-down.
// Las planillas de la jornada (micrófono, carteles de cancha) se generan desde acá
// y no desde la fecha de una zona, porque toman los partidos de todas las zonas
// que juegan el mismo día.
export default function RoundScreen({
  round,
  torneoNombre,
  torneoAno,
  teams = [],
  sedes = [],
  onChange,
  onOpenZone,
  onBack,
}) {
  const [microfonoAbierto, setMicrofonoAbierto] = React.useState(false);
  const [cartelesAbierto, setCartelesAbierto] = React.useState(false);
  const [arbitrosAbierto, setArbitrosAbierto] = React.useState(false);
  const [fichasAbierto, setFichasAbierto] = React.useState(false);

  const rename = (name) => onChange({ ...round, name });

  const addZone = () => {
    // El _id se genera acá (ver ids.js): las fechas que se carguen en la zona se
    // guardan de a una y necesitan poder nombrarla.
    const nueva = {
      id: genId("zone"),
      _id: nuevoObjectId(),
      name: "Zona " + String.fromCharCode(65 + round.zones.length),
      teamIds: [],
      matchDates: [],
    };
    onChange({ ...round, zones: [...round.zones, nueva] });
  };

  // Borrar una zona se lleva sus fechas, partidos y resultados: se avisa cuánto es
  // antes de confirmar.
  const removeZone = (zone) => {
    const partidos = partidosDeFechas(zone.matchDates);
    const texto =
      textoArrastre({
        fechas: zone.matchDates.length,
        partidos: partidos.length,
        resultados: jugados(partidos),
      }) || "La zona todavía está vacía.";

    toastConfirm("¿ Eliminar " + zone.name + " ?", texto, () =>
      onChange({ ...round, zones: round.zones.filter((z) => z.id !== zone.id) })
    );
  };

  return (
    <div className="screen-slide">
      <NavHeader title={round.name} subtitle={round.zones.length + " zonas"} onBack={onBack} />

      <div className="card mb-3">
        <div className="card-body">
          <div className="form-group mb-0">
            <label className="font-weight-bold" style={{ fontSize: "0.875rem" }}>
              Nombre de la Rueda
            </label>
            <input
              type="text"
              className="form-control form-control-lg"
              value={round.name}
              onChange={(e) => rename(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="sub-section-title mb-0">Zonas</div>
        <div className="d-flex flex-wrap justify-content-end" style={{ gap: "0.5rem" }}>
          <Button
            className="btn-arhock btn-sm my-0"
            onClick={() => setArbitrosAbierto(true)}
            disabled={round.zones.length === 0}
          >
            Planilla Árbitros
          </Button>
          <Button
            className="btn-arhock btn-sm my-0"
            onClick={() => setFichasAbierto(true)}
            disabled={round.zones.length === 0}
          >
            Fichas Árbitros
          </Button>
          <Button
            className="btn-arhock btn-sm my-0"
            onClick={() => setMicrofonoAbierto(true)}
            disabled={round.zones.length === 0}
          >
            Planilla Micrófono
          </Button>
          <Button
            className="btn-arhock btn-sm my-0"
            onClick={() => setCartelesAbierto(true)}
            disabled={round.zones.length === 0}
          >
            Carteles Canchas
          </Button>
          <Button className="btn-arhock btn-sm my-0" onClick={addZone}>
            + Agregar Zona
          </Button>
        </div>
      </div>

      {round.zones.length === 0 ? (
        <div className="empty-state" style={{ padding: "2rem 1rem" }}>
          <svg
            className="empty-state-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            style={{ width: "2.5rem", height: "2.5rem" }}
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
          <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
            Agregá zonas para organizar los equipos y las fechas de esta rueda.
          </p>
        </div>
      ) : (
        round.zones.map((zone) => (
          <NavRow
            key={zone.id}
            title={zone.name}
            accent="zone"
            badges={[
              { label: zone.teamIds.length + " equipos", variant: "teams" },
              { label: zone.matchDates.length + " fechas", variant: "dates" },
            ]}
            onClick={() => onOpenZone(zone.id)}
            onDelete={() => removeZone(zone)}
          />
        ))
      )}

      <PlanillaArbitrosModal
        isOpen={arbitrosAbierto}
        round={round}
        torneoNombre={torneoNombre}
        torneoAno={torneoAno}
        teams={teams}
        onClose={() => setArbitrosAbierto(false)}
      />

      <FichasArbitrosModal
        isOpen={fichasAbierto}
        round={round}
        torneoNombre={torneoNombre}
        torneoAno={torneoAno}
        teams={teams}
        onClose={() => setFichasAbierto(false)}
      />

      <PlanillaMicrofonoModal
        isOpen={microfonoAbierto}
        round={round}
        torneoNombre={torneoNombre}
        torneoAno={torneoAno}
        teams={teams}
        sedes={sedes}
        onClose={() => setMicrofonoAbierto(false)}
      />

      <CartelesCanchasModal
        isOpen={cartelesAbierto}
        round={round}
        torneoNombre={torneoNombre}
        torneoAno={torneoAno}
        onClose={() => setCartelesAbierto(false)}
      />
    </div>
  );
}
